// Table sounds (only Michael's picks): check, call, raise, win, your turn.
// Fold is intentionally silent.
//
// Every phone plays the sound from synced state (gameState.lastAction, written
// by roomManager.processAction in the same write as the action), so everyone
// at the table hears it once. The first lastAction a phone sees (page load,
// rejoin, reconnect) is only remembered, never played, and an undo restores an
// older lastAction this phone already saw, so nothing replays.

export const ACTION_SOUND_FILES = {
  CHECK: '/sounds/check.mp3',
  CALL: '/sounds/call.mp3',
  RAISE: '/sounds/raise.mp3',
  WIN: '/sounds/win.mp3',
  TURN: '/sounds/turn.mp3',
};
const ACTION_KINDS = ['CHECK', 'CALL', 'RAISE'];

export const SOUND_MUTED_KEY = 'chipless.soundMuted';

/** Log kind -> sound. ALL_IN uses the raise sound until it has its own. */
export function soundForKind(kind) {
  if (kind === 'ALL_IN') return 'RAISE';
  return ACTION_KINDS.includes(kind) ? kind : null;
}

/** One key per awarded pot (split pots share one winnerInfo -> one sound). */
export function winnerKey(winnerInfo) {
  if (!winnerInfo || !Array.isArray(winnerInfo.winnerIds) || winnerInfo.winnerIds.length === 0) return null;
  return `${winnerInfo.handNumber ?? ''}|${winnerInfo.ts ?? ''}|${winnerInfo.winnerIds.join(',')}`;
}

/** observe(winnerInfo) -> 'WIN' once per new award; first observation primes. */
export function createWinSoundTracker() {
  let primed = false;
  const seen = new Set();
  return function observe(winnerInfo) {
    const key = winnerKey(winnerInfo);
    if (!primed) {
      primed = true;
      if (key) seen.add(key);
      return null;
    }
    if (!key || seen.has(key)) return null;
    seen.add(key);
    return 'WIN';
  };
}

/**
 * observe({ turnId, myId, handNumber, stage }) -> 'TURN' when the turn just
 * became mine. Never on the first observation (load / rejoin), once per turn.
 */
export function createTurnSoundTracker() {
  let primed = false;
  let prevKey = null;
  return function observe({ turnId, myId, handNumber, stage } = {}) {
    const mine = !!myId && turnId === myId;
    const key = mine ? `${handNumber ?? ''}|${stage ?? ''}|${turnId}` : null;
    const wasPrimed = primed;
    primed = true;
    const changed = key !== prevKey;
    prevKey = key;
    if (!wasPrimed || !key || !changed) return null;
    return 'TURN';
  };
}

export function lastActionKey(la) {
  if (!la || typeof la !== 'object' || !la.kind) return null;
  return `${la.ts || 0}|${la.playerId || la.name || ''}|${la.kind}`;
}

/**
 * Returns observe(lastAction) -> sound name to play, or null.
 * The first observation only primes (no sound on load/rejoin).
 */
export function createActionSoundTracker() {
  let primed = false;
  const seen = new Set();
  return function observe(lastAction) {
    const key = lastActionKey(lastAction);
    if (!primed) {
      primed = true;
      if (key) seen.add(key);
      return null;
    }
    if (!key || seen.has(key)) return null;
    seen.add(key);
    return soundForKind(lastAction.kind);
  };
}

export function isSoundMuted(storage = safeStorage()) {
  try {
    return storage?.getItem(SOUND_MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

export function setSoundMuted(muted, storage = safeStorage()) {
  try {
    if (muted) storage?.setItem(SOUND_MUTED_KEY, '1');
    else storage?.removeItem(SOUND_MUTED_KEY);
  } catch {
    /* private mode: mute just won't persist */
  }
}

function safeStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

// ---- Playback (Web Audio; iOS needs a resume() inside a user tap) ----------

let ctx = null;
const buffers = {};
let loading = null;
let unlockBound = false;

// Test hook: every sound this phone tried to play, in order. Read in e2e via
// window.__chiplessSounds.
export const soundLog = [];
if (typeof window !== 'undefined') window.__chiplessSounds = soundLog;

function getCtx() {
  if (ctx) return ctx;
  const AC = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  try {
    ctx = new AC();
  } catch {
    ctx = null;
  }
  return ctx;
}

/** Fetch + decode all five files once. Safe to call repeatedly. */
export function preloadActionSounds() {
  if (loading) return loading;
  const ac = getCtx();
  if (!ac || typeof fetch !== 'function') return Promise.resolve();
  loading = Promise.all(
    Object.entries(ACTION_SOUND_FILES).map(async ([name, url]) => {
      try {
        const res = await fetch(url);
        const data = await res.arrayBuffer();
        buffers[name] = await new Promise((ok, fail) => {
          const p = ac.decodeAudioData(data, ok, fail);
          if (p && typeof p.then === 'function') p.then(ok, fail);
        });
      } catch (e) {
        /* a missing sound must never break the game */
      }
    })
  );
  return loading;
}

/** iOS/Safari start audio suspended; resume on the first tap anywhere. */
export function bindAudioUnlock() {
  if (unlockBound || typeof document === 'undefined') return;
  unlockBound = true;
  const unlock = () => {
    const ac = getCtx();
    if (!ac) return;
    if (ac.state === 'suspended') ac.resume().catch(() => {});
    // A silent one-sample buffer fully unlocks older iOS WebKit.
    try {
      const b = ac.createBuffer(1, 1, 22050);
      const s = ac.createBufferSource();
      s.buffer = b;
      s.connect(ac.destination);
      s.start(0);
    } catch { /* ignore */ }
    if (ac.state === 'running') {
      ['touchend', 'pointerdown', 'click', 'keydown'].forEach((t) =>
        document.removeEventListener(t, unlock, true)
      );
    }
  };
  ['touchend', 'pointerdown', 'click', 'keydown'].forEach((t) =>
    document.addEventListener(t, unlock, true)
  );
}

export function playActionSound(name, { muted = isSoundMuted() } = {}) {
  if (!name) return false;
  soundLog.push({ name, muted, ts: Date.now() });
  if (muted) return false;
  const ac = getCtx();
  const buf = buffers[name];
  if (!ac || !buf) return false;
  try {
    if (ac.state === 'suspended') ac.resume().catch(() => {});
    const src = ac.createBufferSource();
    src.buffer = buf;
    const gain = ac.createGain();
    gain.gain.value = 0.6;
    src.connect(gain);
    gain.connect(ac.destination);
    src.start(0);
    return true;
  } catch {
    return false;
  }
}
